import CustomButton from "../../../../components/ui/customeButton";
import EventTile from "./EventTile";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { useMemo, useState } from "react";
import { IEvents } from "../../../../models/IEvents";
import { eventSpansMonth } from "../../../../utils/date";

type MonthOption = "All Months" | string;
type CategoryOption = "All" | string;

const EventBody = ({ data }: { data: IEvents[] }) => {
  const [monthFilterValue, setMonthFilterValue] =
    useState<MonthOption>("All Months");

  const [countryFilterValue, setCountryFilterValue] = useState("All Locations");

  const [selectedFilter, setSelectedFilter] = useState<CategoryOption>("All");

  const countryList = useMemo(() => {
    return Array.from(
      new Set(data.map((event) => event.country).filter(Boolean)),
    );
  }, [data]);

  const futureSixMonths = useMemo(() => {
    const result: { label: string; value: string }[] = [];
    const now = new Date();

    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);

      result.push({
        label: d.toLocaleString("en-US", {
          month: "short",
          year: "numeric",
        }),
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      });
    }

    return result;
  }, []);

  const categoryList = useMemo(() => {
    return Array.from(
      new Set(data.map((event) => event.category).filter(Boolean)),
    );
  }, [data]);

  const filteredData = useMemo(() => {
    return data.filter((event) => {
      const categoryMatch =
        selectedFilter === "All" || event.category === selectedFilter;

      const countryMatch =
        countryFilterValue === "All Locations" ||
        event.country === countryFilterValue;

      const monthMatch =
        monthFilterValue === "All Months" ||
        eventSpansMonth(event.startDate, event.endDate, monthFilterValue);

      return categoryMatch && countryMatch && monthMatch;
    });
  }, [data, selectedFilter, countryFilterValue, monthFilterValue]);

  return (
    <div className="py-4 flex flex-col gap-4">
      <div className="flex flex-col md:flex-row justify-between gap-4 md:gap-2 items-center">
        <div className="flex gap-2 flex-wrap">
          <CustomButton
            bname="All"
            setSelectedFilter={setSelectedFilter}
            selectedFilter={selectedFilter}
          />

          {categoryList.map((category) => (
            <CustomButton
              key={category}
              bname={category}
              setSelectedFilter={setSelectedFilter}
              selectedFilter={selectedFilter}
            />
          ))}
        </div>

        <div className="flex gap-2">
          <Select
            value={countryFilterValue}
            onValueChange={setCountryFilterValue}
          >
            <SelectTrigger
              className="w-fit gap-2 text-sm leading-7 font-medium
             border-none shadow-none
             focus:outline-none focus:ring-0 focus:border-none
             focus-visible:outline-none focus-visible:ring-0"
              style={{ color: "#d31710" }}
              aria-label="Filter events by location"
            >
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-none">
              <SelectGroup>
                <SelectItem value="All Locations">All Locations</SelectItem>
                {countryList.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Select value={monthFilterValue} onValueChange={setMonthFilterValue}>
            <SelectTrigger
              className="w-fit gap-2 text-sm leading-7 font-medium
             border-none shadow-none
             focus:outline-none focus:ring-0 focus:border-none
             focus-visible:outline-none focus-visible:ring-0"
              style={{ color: "#d31710" }}
              aria-label="Filter events by month"
            >
              <SelectValue placeholder="All Months" />
            </SelectTrigger>

            <SelectContent className="bg-white border border-none">
              <SelectGroup>
                <SelectItem value="All Months">All Months</SelectItem>

                {futureSixMonths.map((month) => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {filteredData.length > 0 ? (
          filteredData.map((event) => <EventTile key={event.id} data={event} />)
        ) : (
          <div className="col-span-12 text-center text-gray-500">
            No events found
          </div>
        )}
      </div>
    </div>
  );
};

export default EventBody;
